import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, CheckCircle2, XCircle, MinusCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/config/api';

// Every row comes from a live check made by the backend
// (GET /api/platform/integrations/status, super admin only). Nothing here
// is ever shown as working unless the provider answered.
const WEB_GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const GROUPS = [
  ['payments', 'Payments'],
  ['messaging', 'Messaging'],
  ['google', 'Google'],
  ['ai', 'AI providers'],
  ['storage', 'Storage'],
  ['services', 'chain29 services'],
];

function statusOf(row) {
  if (!row.configured) return { label: row.optional ? 'Not set (optional)' : 'Not configured', tone: 'grey', Icon: MinusCircle };
  if (row.ok === true) return { label: 'Working', tone: 'green', Icon: CheckCircle2 };
  if (row.ok === false) return { label: 'Failing', tone: 'red', Icon: XCircle };
  return { label: 'Configured, not checked', tone: 'grey', Icon: MinusCircle };
}

const TONE = {
  green: 'text-green-400',
  red: 'text-red-400',
  grey: 'text-slate-400',
};

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export default function IntegrationsStatusPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/platform/integrations/status`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { refresh: refresh || undefined, web_google_client_id: WEB_GOOGLE_CLIENT_ID || undefined },
        timeout: 30000,
      });
      setData(res.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403) setError('Only a super admin can see integration status.');
      else if (status === 401) setError('Your session has expired. Sign in again.');
      else setError(`Could not check integrations: ${typeof detail === 'string' ? detail : (err.message || 'no answer')}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(false); }, [load]);

  const rows = data?.integrations || [];
  const counts = rows.reduce((acc, r) => {
    acc[statusOf(r).tone] += 1;
    return acc;
  }, { green: 0, red: 0, grey: 0 });

  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="integrations-status">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-white">Integrations</CardTitle>
          <CardDescription className="text-slate-400">
            Live check of each service chain29 uses.
            {data?.checked_at && (
              <> Last checked {when(data.checked_at)}{data.cached ? ' (cached)' : ''}.</>
            )}
          </CardDescription>
          {rows.length > 0 && (
            <p className="text-sm mt-1">
              <span className="text-green-400">{counts.green} working</span>
              {' · '}<span className="text-red-400">{counts.red} failing</span>
              {' · '}<span className="text-slate-400">{counts.grey} not configured / not checked</span>
            </p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="border-slate-600 text-slate-300"
          onClick={() => load(true)}
          disabled={loading}
          data-testid="integrations-check-again"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Check again
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <div className="p-4 rounded-lg bg-red-950/40 border border-red-800 text-red-300 text-sm" role="alert">
            {error}
          </div>
        )}
        {loading && !data && !error && (
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking every integration (up to a few seconds)…
          </div>
        )}
        {GROUPS.map(([group, title]) => {
          const groupRows = rows.filter(r => r.group === group);
          if (!groupRows.length) return null;
          return (
            <div key={group} className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">{title}</h3>
              {groupRows.map(row => {
                const s = statusOf(row);
                return (
                  <div
                    key={row.key}
                    className="flex items-start justify-between gap-4 p-4 bg-slate-700 rounded-lg"
                    data-testid={`integration-${row.key}`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <s.Icon className={`h-5 w-5 mt-0.5 shrink-0 ${TONE[s.tone]}`} />
                      <div className="min-w-0">
                        <p className="font-medium text-white">{row.name}</p>
                        <p className="text-sm text-slate-300 break-words">{row.detail}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-sm font-medium ${TONE[s.tone]}`}>{s.label}</p>
                      <p className="text-xs text-slate-400">
                        {row.latency_ms != null ? `${row.latency_ms} ms · ` : ''}{when(row.checked_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
